import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import HistoryIcon from '@mui/icons-material/History';

export interface NavigationTarget {
  id: string;
  title: string;
  section: 'Results' | 'Charts' | 'History';
  description: string;
  destination: 'results' | 'charts' | 'history';
  tabIndex?: number;
  keywords?: string[];
  disabled?: boolean;
}

interface NavigationSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (target: NavigationTarget) => void;
  targets: NavigationTarget[];
}

const getSectionIcon = (section: NavigationTarget['section']) => {
  switch (section) {
    case 'Results':
      return <AssessmentIcon color="primary" />;
    case 'Charts':
      return <ShowChartIcon color="primary" />;
    case 'History':
      return <HistoryIcon color="primary" />;
    default:
      return <SearchIcon color="primary" />;
  }
};

const NavigationSearchDialog: React.FC<NavigationSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  targets,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  const filteredTargets = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const getScore = (target: NavigationTarget) => {
      if (!normalizedQuery) {
        return 0;
      }

      const title = target.title.toLowerCase();
      const combinedText = [
        target.title,
        target.section,
        target.description,
        ...(target.keywords ?? []),
      ]
        .join(' ')
        .toLowerCase();

      if (title.startsWith(normalizedQuery)) {
        return 400;
      }

      if (title.includes(normalizedQuery)) {
        return 300;
      }

      if (combinedText.includes(normalizedQuery)) {
        return 200;
      }

      const terms = normalizedQuery.split(/\s+/).filter(Boolean);
      if (terms.length > 0 && terms.every((term) => combinedText.includes(term))) {
        return 150;
      }

      return -1;
    };

    return targets
      .map((target) => ({ target, score: getScore(target) }))
      .filter(({ score }) => score >= 0)
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        if (a.target.section !== b.target.section) {
          return a.target.section.localeCompare(b.target.section);
        }

        return a.target.title.localeCompare(b.target.title);
      })
      .map(({ target }) => target);
  }, [query, targets]);

  const handleSelect = (target: NavigationTarget) => {
    if (target.disabled) {
      return;
    }

    onSelect(target);
    onClose();
  };

  const firstEnabledTarget = filteredTargets.find((target) => !target.disabled);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Search Navigation</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && firstEnabledTarget) {
              event.preventDefault();
              handleSelect(firstEnabledTarget);
            }
          }}
          placeholder="Search results, charts, statistics, or history"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Press Enter to jump to the first matching item.
        </Typography>

        {filteredTargets.length > 0 ? (
          <List sx={{ mt: 2, maxHeight: 420, overflowY: 'auto', py: 0 }}>
            {filteredTargets.map((target) => (
              <ListItemButton
                key={target.id}
                disabled={target.disabled}
                onClick={() => handleSelect(target)}
                sx={{
                  mb: 0.75,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  {getSectionIcon(target.section)}
                </ListItemIcon>
                <ListItemText
                  primary={target.title}
                  secondary={target.description}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondaryTypographyProps={{ sx: { mt: 0.25 } }}
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 1 }}>
                  <Chip label={target.section} size="small" variant="outlined" />
                  {target.disabled && (
                    <Chip label="Unavailable" size="small" color="default" />
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box
            sx={{
              mt: 3,
              py: 6,
              px: 2,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              No matching destinations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try terms like `statistics`, `qti`, `energy`, or `history`.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NavigationSearchDialog;
